import { useEffect, useState } from "react";
import { Loader2, Mail, Palette, Save, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import ColorField from "@/components/admin/ColorField";

const DEFAULTS = {
  primary: "#e8630a",
  accent: "#06b6d4",
  bg: "#162235",
  text: "#ffffff",
  footer_note: "",
};

export default function AdminOrgTheme({ org, canEdit, onSaved }) {
  const { authAxios } = useAuth();
  const [theme, setTheme] = useState({ ...DEFAULTS, ...(org?.ticket_theme || {}) });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    setTheme({ ...DEFAULTS, ...(org?.ticket_theme || {}) });
  }, [org?.id, org?.ticket_theme]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await authAxios.patch(`/admin/organizations/${org.id}/theme`, { ticket_theme: theme });
      setTheme(data.ticket_theme);
      toast.success("Ticket-uiterlijk opgeslagen");
      onSaved?.(data.ticket_theme);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await authAxios.post(`/admin/organizations/${org.id}/test-email`);
      toast.success(`Test-mail verzonden naar ${data.to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setTesting(false);
    }
  };

  const reset = () => setTheme({ ...DEFAULTS });

  return (
    <div className="mt-6 rounded-xl border border-svj-border bg-svj-bg/40 p-5" data-testid={`org-theme-${org?.id}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Palette className="h-4 w-4 text-svj-cyan" />
          <div className="text-sm font-semibold text-white uppercase tracking-wider">Ticket-uiterlijk</div>
        </div>
        {canEdit && (
          <button type="button" onClick={reset} className="text-xs text-slate-400 hover:text-white inline-flex items-center gap-1">
            <Undo2 className="h-3 w-3" /> Standaard
          </button>
        )}
      </div>

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-3">
          <ColorField label="Primair (gradient/header)" value={theme.primary} onChange={(v) => setTheme({ ...theme, primary: v })} testid={`theme-${org?.id}-primary`} />
          <ColorField label="Accent (cyaan)" value={theme.accent} onChange={(v) => setTheme({ ...theme, accent: v })} testid={`theme-${org?.id}-accent`} />
          <ColorField label="Achtergrond kaart" value={theme.bg} onChange={(v) => setTheme({ ...theme, bg: v })} testid={`theme-${org?.id}-bg`} />
          <ColorField label="Tekst" value={theme.text} onChange={(v) => setTheme({ ...theme, text: v })} testid={`theme-${org?.id}-text`} />
          <label className="block">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Voetnoot op ticket + e-mail (optioneel)</span>
            <input
              value={theme.footer_note || ""}
              onChange={(e) => setTheme({ ...theme, footer_note: e.target.value })}
              placeholder="Bijv. 'Neem legitimatie mee'"
              maxLength={120}
              data-testid={`theme-${org?.id}-footer`}
              className="mt-1 w-full h-10 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan"
            />
          </label>
          <label className="block">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">E-mail intro (extra tekst in bevestigingsmail)</span>
            <textarea
              value={theme.email_intro || ""}
              onChange={(e) => setTheme({ ...theme, email_intro: e.target.value })}
              rows={2}
              maxLength={280}
              placeholder="Bijv. 'We kijken er naar uit je te zien op onze feestlocatie!'"
              data-testid={`theme-${org?.id}-email-intro`}
              className="mt-1 w-full px-3 py-2 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan resize-y"
            />
            <div className="mt-1 text-[10px] text-slate-500">
              Het logo van deze organisatie ({org?.name}) staat automatisch in de e-mail-header.
              {!org?.logo_url && " ← geen logo geüpload, dus de mail toont alleen tekst."}
            </div>
          </label>
        </div>

        {/* Live preview */}
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-2">Ticket-voorbeeld</div>
          <div className="rounded-xl overflow-hidden border border-svj-border" style={{ backgroundColor: theme.bg, color: theme.text }}>
            <div style={{ background: `linear-gradient(90deg, ${theme.primary}, ${theme.accent})`, padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
              <div>
                <div className="text-[10px] uppercase tracking-widest opacity-80">Ticket</div>
                <div className="text-lg font-extrabold" style={{ lineHeight: 1.15 }}>{org?.name || "Jouw organisatie"}</div>
                <div className="text-[11px] opacity-90">za 29 aug 2026 · 08:00 · Voorbeeldlocatie</div>
              </div>
              {org?.logo_url && (
                <div style={{ width: 44, height: 44, borderRadius: 9999, background: "#fff", overflow: "hidden", flexShrink: 0 }}>
                  <img src={org.logo_url} alt="logo" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                </div>
              )}
            </div>
            <div className="flex" style={{ borderTop: `1px dashed ${theme.text}22` }}>
              <div className="flex-1 p-5">
                <div className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: theme.accent }}>Tickettype</div>
                <div className="font-bold">Toegang</div>
                <div className="mt-3 text-[10px] font-semibold uppercase tracking-widest" style={{ color: theme.accent }}>Ticketcode</div>
                <div className="font-mono text-xs">SVJ-VOORBEELDCODE</div>
                {theme.footer_note && (
                  <div className="mt-4 text-[11px] italic opacity-80">{theme.footer_note}</div>
                )}
              </div>
              <div className="w-24 sm:w-28 flex items-center justify-center bg-white">
                <img alt="qr" src="data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><rect width='100' height='100' fill='white'/><g fill='black'><rect x='10' y='10' width='20' height='20'/><rect x='70' y='10' width='20' height='20'/><rect x='10' y='70' width='20' height='20'/><rect x='40' y='40' width='10' height='10'/><rect x='55' y='30' width='8' height='8'/><rect x='35' y='60' width='8' height='8'/><rect x='60' y='55' width='10' height='10'/><rect x='75' y='55' width='6' height='6'/><rect x='60' y='75' width='6' height='6'/><rect x='75' y='75' width='10' height='10'/></g></svg>" className="w-16 h-16" />
              </div>
            </div>
          </div>

          <div className="mt-4 text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-2">E-mail-voorbeeld</div>
          <div className="rounded-xl overflow-hidden border border-svj-border" style={{ backgroundColor: theme.bg, color: theme.text }}>
            <div style={{ background: `linear-gradient(90deg, ${theme.primary}, ${theme.accent})`, padding: "18px 20px", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
              <div>
                <div className="text-[10px] uppercase tracking-widest opacity-85 font-bold">Bevestiging</div>
                <div className="text-base font-extrabold mt-1">Voorbeeld event</div>
                <div className="text-[11px] opacity-85">za 29 aug 2026 · 08:00</div>
              </div>
              {org?.logo_url && (
                <div style={{ width: 40, height: 40, borderRadius: 9999, background: "#fff", overflow: "hidden", flexShrink: 0 }}>
                  <img src={org.logo_url} alt="logo" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                </div>
              )}
            </div>
            <div className="p-4 text-xs">
              <div>Hoi Voorbeeld,</div>
              <div className="opacity-80">bedankt voor je bestelling bij <b style={{ color: theme.text }}>{org?.name}</b>.</div>
              {theme.email_intro && <div className="mt-2 italic opacity-80">{theme.email_intro}</div>}
              <div className="mt-3 inline-block rounded-full text-white font-bold text-[10px] px-3 py-1" style={{ background: `linear-gradient(90deg, ${theme.primary}, ${theme.accent})` }}>Open mijn tickets</div>
            </div>
          </div>
        </div>
      </div>

      {canEdit && (
        <div className="mt-6 flex justify-between items-center gap-3">
          <button type="button" onClick={sendTest} disabled={testing} data-testid={`theme-testmail-${org?.id}`} className="rounded-full border-2 border-svj-cyan text-svj-cyan font-bold text-sm px-5 py-2 inline-flex items-center gap-2 hover:bg-svj-cyan/10">
            {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
            Stuur test-mail
          </button>
          <button type="button" onClick={save} disabled={saving} data-testid={`theme-save-${org?.id}`} className="rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-sm px-5 py-2 inline-flex items-center gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Uiterlijk opslaan
          </button>
        </div>
      )}
    </div>
  );
}
